/**
 * LE PREFERENZE DELLA PERSONA, LETTE UNA VOLTA SOLA ALL'AVVIO.
 *
 * Il contratto e il perche' stanno in `preferenzeContext.ts`. Qui c'e' la chiamata, il cancello
 * e la scrittura.
 *
 * ## Il cancello, che e' la parte che si puo' sbagliare
 *
 * Finche' la lettura non e' tornata, sotto non si disegna niente. Serve a non far vedere il
 * salto — la scheda che si apre come finestra e diventa colonna un istante dopo, la barra del
 * telefono che cambia forma sotto il dito — ed e' il motivo per cui lo specchio in
 * `localStorage` si e' potuto togliere.
 *
 * Tre cose lo rendono un cancello e non una trappola:
 *
 * · **si apre comunque dopo `ATTESA_MASSIMA`.** Una rete lenta o assente non deve tenere
 *   l'applicazione chiusa: scaduta l'attesa si va avanti con i predefiniti, e i valori veri
 *   arrivano quando arrivano. In quel caso il salto si rivede — ed e' giusto cosi': e' il caso
 *   raro, e l'alternativa e' una schermata bianca senza fine.
 * · **senza sessione non si aspetta niente.** `user-preferences` vuole un token; sulla pagina
 *   di accesso non c'e' nessuno di cui leggere le preferenze, quindi il cancello e' gia' aperto.
 * · **si apre una volta sola.** Al login `userId` compare e la lettura riparte: senza il
 *   fermo, l'applicazione appena montata si smonterebbe per un istante e si rimonterebbe.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createOne, fetchAllPages, updateOne } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { notifyWarn } from '@/shared/lib/notify';
import { PreferencesContext, type PreferencesStore, type PreferenceEntry } from './preferenzeContext';

interface PreferenceRow extends Denormalized {
  prefKey?: string;
  prefValue?: string;
}

/**
 * Oltre questa attesa si va avanti senza.
 *
 * Un secondo e mezzo: abbastanza perche' una chiamata normale ce la faccia con margine, poco
 * abbastanza da non sembrare un'applicazione che non parte. Non e' un timeout della richiesta —
 * quella prosegue e il valore arriva lo stesso, solo piu' tardi.
 */
const MAX_WAIT = 1500;

export const PREFERENCES_QUERY_KEY = 'preferenze-utente';

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const userId = useCurrentUserId();
  const qc = useQueryClient();
  const queryKey = [PREFERENCES_QUERY_KEY, userId];

  const q = useQuery({
    queryKey: queryKey,
    enabled: Boolean(userId),
    queryFn: async () => {
      /*
       * Tutte le righe della persona: il server le filtra gia' per `UserId` e `TenantId` dal
       * token (`UserPreferenceResourceDefinition`), quindi qui non si filtra niente — e non si
       * deve, perche' mandare l'id dal client significherebbe far decidere a lui una cosa che
       * il server tiene apposta non falsificabile.
       *
       * `fetchAllPages` con un tetto basso: sono le preferenze di **una** persona, poche
       * decine. Il tetto e' un paracadute, non una previsione.
       */
      const rows = await fetchAllPages<PreferenceRow>('user-preferences', undefined, 500);
      const map = new Map<string, PreferenceEntry>();
      for (const r of rows) {
        if (r.prefKey) map.set(r.prefKey, { id: r.id, text: r.prefValue ?? '' });
      }
      return map;
    },
  });

  /* L'attesa massima: parte con la sessione e non si riarma. */
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!userId) return;
    const t = setTimeout(() => setExpired(true), MAX_WAIT);
    return () => clearTimeout(t);
  }, [userId]);

  /*
   * ⚠️ Il fermo si scrive **durante il render**, di proposito: e' un chiavistello, non uno
   * stato. Con un `useState` + effetto il cancello si aprirebbe un render dopo, cioe' si
   * vedrebbe un giro di schermata vuota proprio nel caso in cui tutto e' andato veloce.
   */
  const alreadyOpen = useRef(false);
  const ready = alreadyOpen.current || !userId || !q.isLoading || expired;
  alreadyOpen.current = ready;

  const save = useMutation({
    mutationFn: async ({ itemKey: key, text }: { itemKey: string; text: string }) => {
      /*
       * ⚠️ L'id viene dal magazzino, non da una rilettura.
       *
       * `UserPreference` e' unico su `(TenantId, UserId, PrefKey)`: una `create` di troppo non
       * e' un doppione innocuo, e' un rifiuto del server. Prima qui c'era una lettura per ogni
       * scrittura, perche' la cache per-chiave era vecchia proprio nei due momenti in cui
       * serviva. Con una mappa sola quel problema non c'e': la scrittura ottimistica ci mette
       * dentro l'id appena creato, quindi la seconda scelta di fila trova gia' la riga.
       */
      const current = qc.getQueryData<Map<string, PreferenceEntry>>(queryKey);
      const existing = current?.get(key);
      if (existing?.id) {
        await updateOne('user-preferences', { id: existing.id, attributes: { prefValue: text } });
        return { key, text, id: existing.id };
      }
      const created = await createOne<PreferenceRow>('user-preferences', {
        attributes: { prefKey: key, prefValue: text },
      });
      return { key, text, id: created?.id ?? '' };
    },
    onSuccess: ({ key: chiave, text: testo, id }) => {
      /* Si mette l'id vero al posto di quello provvisorio: la prossima scrittura aggiorna. */
      qc.setQueryData<Map<string, PreferenceEntry>>(queryKey, (m) => {
        const copy = new Map(m ?? []);
        copy.set(chiave, { id, text: testo });
        return copy;
      });
    },
    /*
     * ⚠️ Il fallimento si dice, e si dice **preciso**. Senza lo specchio locale la frase di
     * prima — «vale su questo dispositivo ma non e' stata salvata sul tuo profilo» — sarebbe
     * diventata falsa: non vale piu' da nessuna parte oltre questa schermata aperta.
     */
    onError: () => notifyWarn(
      'La scelta vale finche\' resti su questa pagina: non e\' stata salvata sul tuo profilo, '
      + 'quindi ricaricando e sugli altri dispositivi resta quella di prima.',
      'Preferenza non salvata',
    ),
  });

  const { mutateAsync: saveHour } = save;

  const warehouse = useMemo<PreferencesStore>(() => ({
    ready,
    read: (chiave) => q.data?.get(chiave)?.text,
    write: (chiave, testo) => {
      /*
       * Il magazzino cambia **subito**, senza aspettare la risposta: chi ha appena scelto deve
       * vedere l'effetto nello stesso giro. L'id resta quello che c'era (vuoto se la riga non
       * esiste ancora) e lo rimpiazza `onSuccess`.
       */
      qc.setQueryData<Map<string, PreferenceEntry>>(queryKey, (m) => {
        const copia = new Map(m ?? []);
        copia.set(chiave, { id: copia.get(chiave)?.id ?? '', text: testo });
        return copia;
      });
      /* Non rifiuta mai: l'avviso all'utente l'ha gia' dato `onError`, e qui l'esito serve solo
         a chi deve decidere se buttare via una copia vecchia. */
      return saveHour({ itemKey: chiave, text: testo }).then(() => true, () => false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [ready, q.data, qc, saveHour, userId]);

  if (!ready) return null;

  return <PreferencesContext.Provider value={warehouse}>{children}</PreferencesContext.Provider>;
}
